import { Button, ButtonColors } from '@chiroup/components/Button';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import Combobox from '@chiroup/components/form/Combobox';
import { Input } from '@chiroup/components/form/Input';
import { InputMasked } from '@chiroup/components/form/InputMasked';
import { Select } from '@chiroup/components/form/Select';
import { ZipCodeInput } from '@chiroup/components/form/ZipCodeInput';
import { Icons } from '@chiroup/components/icons';
import { SuffixOptions } from '@chiroup/core/constants/Suffixes';
import { CANADIAN_PROVINCES, STATES } from '@chiroup/core/constants/UsStatesAndTerritories';
import { COUNTRIES } from '@chiroup/core/constants/countries';
import { enumToArrayOfOptions } from '@chiroup/core/functions/enumToArrayOfOptions';
import {
  HealthcomInsurance,
  InsuranceSexTypes,
  InsuranceSexTypesSpanish,
  PatientInsuranceTypes,
  PatientInsuranceTypesSpanish,
  PrimaryInsuredTypesSpanishSurvey,
  PrimaryInsuredTypesSurvey,
} from '@chiroup/core/types/PatientInsurance.type';
import { RequestStep } from '@chiroup/core/types/Request.type';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import requestService from '../../services/request.service';
import DatabaseAutocomplete from './DatabaseAutocomplete';
import UploadInsuranceSide from './UploadInsuranceSide';

// Ported from healthcom-app's `Insurance.tsx`. Behavior, fields,
// validation, and copy are intentionally identical; the only changes
// are:
//   - props replace the legacy `MeContext` (primaryColor)
//   - `complete` callback is the schedule app's `onComplete`
//   - submits go through `requestService.insuranceCreate` /
//     `skipInsuranceCreate` which already route via `prMap.insurance`
//
// Keep this in sync with the legacy component until the legacy app
// is decommissioned — patients see an identical form on both surfaces.

type Props = {
  requestId: string;
  validationCode: string | number;
  language: 'en' | 'es';
  primaryColor: string;
  alreadyComplete?: boolean;
  kiosk?: boolean;
  prMap?: { [key: string]: string };
  clinicId?: number;
  step?: RequestStep;
  onComplete: () => void;
};

const insuredVerbiage = {
  en: {
    title: 'Primary Insured',
    fnameLabel: 'First Name',
    mnameLabel: 'Middle Name',
    lnameLabel: 'Last Name',
    suffix: 'Suffix',
    dateOfBirthLabel: 'Date of Birth',
    sexLabel: 'Sex',
    streetAddressLabel: 'Street Address',
    cityLabel: 'City',
    stateLabel: 'State',
    zipCodeLabel: 'Zip/Postal Code',
    country: 'Country',
    sameAsSelf: 'Address same as self',
  },
  es: {
    title: 'Asegurado primario',
    fnameLabel: 'Nombre de pila',
    mnameLabel: 'Segundo nombre',
    lnameLabel: 'Apellido',
    suffix: 'Sufijo',
    dateOfBirthLabel: 'Fecha de nacimiento',
    sexLabel: 'Sexo',
    streetAddressLabel: 'Dirección',
    cityLabel: 'Ciudad',
    stateLabel: 'Estado',
    zipCodeLabel: 'Código Postal',
    country: 'País',
    sameAsSelf: 'Dirección igual que yo',
  },
};

const verbiage = {
  en: {
    skip: 'I have no insurance coverage to report',
    expired: 'This request has expired.',
    payorLabel: 'Payor',
    memberIdLabel: 'Member ID Number *',
    planNumberLabel: 'Plan or Group Number',
    typeLabel: 'Insurance Type',
    relationshipLabel: 'Relationship to Insured *',
    next: 'Next',
    addAnother: 'Add Another Policy',
    tryAgain: 'Try again',
    memberIdRequired: 'Member ID is required',
    primaryInsuredRequired: 'Relationship to insured is required',
  },
  es: {
    skip: 'No tengo cobertura de seguro para reportar',
    expired: 'Esta solicitud ha caducado.',
    payorLabel: 'Empresa de Seguro',
    memberIdLabel: 'Número de identificación de miembro *',
    planNumberLabel: 'Número de plan o grupo',
    typeLabel: 'Tipo de seguro',
    relationshipLabel: 'Relación con el asegurado *',
    next: 'Continuar',
    addAnother: 'Agregar Póliza',
    tryAgain: 'Intentar otra vez',
    memberIdRequired: 'Se requiere identificación de miembro',
    primaryInsuredRequired: 'Se requiere relación con el asegurado',
  },
};

type Status = 'ready' | 'submitting' | 'submitted' | 'error';

type InsuranceFormValue = Partial<
  HealthcomInsurance & {
    insuranceFrontPreview?: string | null;
    insuranceBackPreview?: string | null;
    errors?: {
      payor?: { message: string };
      memberId?: { message: string };
      planGroupId?: { message: string };
      type?: { message: string };
      primaryInsuredType?: { message: string };
    };
  }
>;

const StepInsurance: React.FC<Props> = ({
  requestId,
  validationCode,
  language,
  primaryColor,
  alreadyComplete,
  kiosk,
  prMap,
  clinicId = -1,
  step,
  onComplete,
}) => {
  const [status, setStatus] = useState<Status>(alreadyComplete ? 'submitted' : 'ready');
  const [insurances, setInsurances] = useState<InsuranceFormValue[]>([{}]);
  const [skipping, setSkipping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [uploadsInProgress, setUploadsInProgress] = useState(false);

  useEffect(() => {
    if (alreadyComplete) setStatus('submitted');
  }, [alreadyComplete]);

  const v = verbiage[language === 'es' ? 'es' : 'en'];
  const iv = insuredVerbiage[language === 'es' ? 'es' : 'en'];

  const getTypesOptions = useMemo(
    () => (index: number) => {
      const options = enumToArrayOfOptions(
        language === 'es' ? PatientInsuranceTypesSpanish : PatientInsuranceTypes
      );

      return options.filter(option => {
        const chosenInForm = insurances.find(
          (insurance, insuranceIndex) => insurance.type === option.value && insuranceIndex !== index
        );

        return !chosenInForm;
      });
    },
    [insurances, language]
  );

  const buttonRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const skip = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      setSkipping(true);
      await requestService.skipInsuranceCreate(requestId, validationCode, kiosk, prMap);
      setStatus('submitted');
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Insurance skip failed';

      if (errorMessage === 'Insurance has already been submitted.') {
        setStatus('submitted');
      } else {
        console.error(err);
        Sentry.captureException(new Error(errorMessage));
      }
    } finally {
      setSkipping(false);
      submittingRef.current = false;
    }
  };

  const onSubmit = async () => {
    if (submittingRef.current) return;

    if (uploadsInProgress) {
      const message =
        language === 'es'
          ? 'Por favor espere a que se completen las cargas de las imágenes de su tarjeta de seguro antes de continuar.'
          : 'Please wait for your insurance card image uploads to complete before continuing.';

      alert(message);

      return;
    }

    const errors: any = {};

    insurances.forEach((insurance, index) => {
      const insuranceErrors: any = {};

      if (!insurance.memberId) {
        insuranceErrors.memberId = { message: v.memberIdRequired };
      }

      if (!insurance.primaryInsuredType) {
        insuranceErrors.primaryInsuredType = { message: v.primaryInsuredRequired };
      }

      if (Object.keys(insuranceErrors).length) errors[index] = insuranceErrors;
    });

    if (Object.keys(errors).length) {
      setInsurances(prev => {
        const next = [...prev];

        next.forEach((insurance, index) => {
          if (errors[index]) insurance.errors = errors[index];
        });

        return next;
      });

      return;
    }

    try {
      submittingRef.current = true;
      setIsSubmitting(true);

      const cleanedInsurances = insurances.map(insurance => {
        const cleaned: any = { ...insurance };
        delete cleaned.errors;
        delete cleaned.insuranceFrontPreview;
        delete cleaned.insuranceBackPreview;

        return cleaned as Partial<HealthcomInsurance>;
      });

      await requestService.insuranceCreate(
        requestId,
        validationCode,
        cleanedInsurances,
        kiosk,
        prMap
      );

      setStatus('submitted');
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Insurance submission failed';

      if (errorMessage === 'Insurance has already been submitted.') {
        setStatus('submitted');
      } else {
        console.error('Error in insurance submission:', err);
        Sentry.captureException(new Error(errorMessage));
        alert(v.tryAgain);
      }
    } finally {
      setIsSubmitting(false);
      submittingRef.current = false;
    }
  };

  const onUploadInsuranceCard = (side: 'front' | 'back', url: string, index: number) => {
    setUploadsInProgress(false);
    const prop = side === 'front' ? 'insuranceFront' : 'insuranceBack';

    setInsurances(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [prop]: url };

      return next;
    });
  };

  const addPolicy = () => setInsurances(prev => [...prev, {}]);

  const onChangeInsurance = (index: number, property: string) => (val: any) =>
    setInsurances(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [property]: val };

      return next;
    });

  const onChangeInsurancePrimaryInsured = (index: number, property: string) => (val: any) =>
    setInsurances(prev => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        primaryInsured: { ...next[index].primaryInsured, [property]: val },
      };

      return next;
    });

  if (status === 'submitted') {
    return (
      <div
        className="rounded-3xl overflow-hidden p-8 text-center flex flex-col items-center gap-3 dark:ring-1 dark:ring-white/10"
        style={{ backgroundColor: `${primaryColor}12` }}
      >
        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {language === 'es' ? 'Información de seguro guardada' : 'Insurance information saved'}
        </p>
        <Button
          text={language === 'es' ? 'Continuar' : 'Continue'}
          onClick={onComplete}
          className="mt-2"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center px-4 sm:min-h-screen">
      <div className="w-full min-w-0 max-w-4xl py-8">
        <h2 className="text-4xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-5xl sm:leading-none md:text-6xl">
          {language === 'es' ? 'Seguro' : step?.title || 'Insurance'}
        </h2>
        <h3 className="mt-2 text-xl font-extrabold leading-6 tracking-tight text-gray-900 sm:text-2xl sm:leading-none md:text-4xl">
          <span style={{ color: primaryColor }}>
            {language === 'es' ? 'Información sobre tu seguro' : step?.description}
          </span>
        </h3>

        <form>
          {insurances.map((insurance, index) => (
            <div key={index} className="relative">
              {insurances.length > 1 && index > 0 && (
                <XMarkIcon
                  className="absolute top-6 right-0 w-6 h-6 mt-2 mr-2 text-gray-400 hover:text-gray-500 cursor-pointer"
                  onClick={() => {
                    setInsurances(prev => {
                      const next = [...prev];
                      next.splice(index, 1);

                      return next;
                    });
                  }}
                />
              )}
              <div className="pt-8 space-y-1 sm:space-y-0 sm:grid sm:grid-cols-4 sm:gap-4">
                {index === 0 && (
                  <Button
                    text={v.skip}
                    onClick={skip}
                    disabled={isSubmitting || skipping}
                    loading={skipping}
                    className="col-span-4 mb-4"
                  />
                )}
                <DatabaseAutocomplete
                  value={insurance.payor as number | undefined}
                  className="col-span-4"
                  onChange={onChangeInsurance(index, 'payor')}
                  buttonRef={buttonRef}
                  searchRef={searchRef}
                  clinicId={clinicId}
                  label={v.payorLabel}
                  error={insurance.errors?.payor}
                  limit={10000}
                  hideEndOfList
                  unshiftNotListedOption={true}
                  language={language}
                />
                <Input
                  name={`memberId[${index}]`}
                  value={insurance.memberId}
                  label={v.memberIdLabel}
                  className="col-span-2"
                  onChange={(val: string) =>
                    onChangeInsurance(index, 'memberId')(val?.replace(/[^a-zA-Z0-9]/g, ''))
                  }
                  errors={insurance.errors?.memberId}
                />
                <Input
                  name={`planNumber[${index}]`}
                  label={v.planNumberLabel}
                  value={insurance.planGroupId}
                  className="col-span-2"
                  onChange={onChangeInsurance(index, 'planGroupId')}
                  errors={insurance.errors?.planGroupId}
                />
                <Combobox
                  name={`type[${index}]`}
                  label={v.typeLabel}
                  options={getTypesOptions(index)}
                  value={insurance.type}
                  onChange={onChangeInsurance(index, 'type')}
                  className="col-span-2"
                  limit={1}
                  errors={insurance.errors?.type}
                  clearable
                  noDarkMode
                />
                <Select
                  name={`relationship[${index}]`}
                  label={v.relationshipLabel}
                  options={enumToArrayOfOptions(
                    language === 'es' ? PrimaryInsuredTypesSpanishSurvey : PrimaryInsuredTypesSurvey
                  )}
                  value={insurance.primaryInsuredType}
                  onChange={onChangeInsurance(index, 'primaryInsuredType')}
                  className="col-span-2"
                  errors={insurance.errors?.primaryInsuredType}
                  noDarkMode
                />
                {insurance?.primaryInsuredType &&
                  insurance?.primaryInsuredType !== PrimaryInsuredTypesSurvey.Self && (
                    <div className="col-span-4 border rounded-md">
                      <div className="p-4">
                        <div className="rounded-lg">
                          <div className="mb-6 sm:mb-0">
                            <h3 className="text-lg font-medium leading-6 text-gray-900 print:text-md">
                              {iv.title}
                            </h3>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 space-y-1 sm:space-y-0 sm:grid sm:grid-cols-4 sm:gap-4">
                        <Input
                          name="primaryInsuredFname"
                          className="col-span-2"
                          label={iv.fnameLabel}
                          value={insurance.primaryInsured?.fname}
                          onChange={onChangeInsurancePrimaryInsured(index, 'fname')}
                        />
                        <Input
                          name="primaryInsuredMname"
                          className="col-span-2"
                          label={iv.mnameLabel}
                          value={insurance.primaryInsured?.mname}
                          onChange={onChangeInsurancePrimaryInsured(index, 'mname')}
                        />
                        <Input
                          name="primaryInsuredLname"
                          className="col-span-2"
                          label={iv.lnameLabel}
                          value={insurance.primaryInsured?.lname}
                          onChange={onChangeInsurancePrimaryInsured(index, 'lname')}
                        />
                        <Select
                          name="suffix"
                          className="col-span-2"
                          label={iv.suffix}
                          options={SuffixOptions}
                          onChange={onChangeInsurancePrimaryInsured(index, 'suffix')}
                          value={insurance.primaryInsured?.suffix}
                        />
                        <InputMasked
                          className="col-span-2"
                          value={insurance.primaryInsured?.dateOfBirth}
                          onChange={onChangeInsurancePrimaryInsured(index, 'dateOfBirth')}
                          name="primaryInsuredDateOfBirth"
                          label={iv.dateOfBirthLabel}
                          patternFormat="##/##/####"
                          placeholder="MM/DD/YYYY"
                        />
                        <Select
                          name="primaryInsuredSex"
                          label={iv.sexLabel}
                          className="col-span-2"
                          value={insurance.primaryInsured?.sex}
                          onChange={onChangeInsurancePrimaryInsured(index, 'sex')}
                          options={enumToArrayOfOptions(
                            language === 'es' ? InsuranceSexTypesSpanish : InsuranceSexTypes
                          )}
                          noDarkMode
                        />
                        <Checkbox
                          name="addressSameAsPatient"
                          label={iv.sameAsSelf}
                          className="col-span-4 pt-2"
                          value={insurance.primaryInsured?.addressSameAsPatient}
                          onChange={onChangeInsurancePrimaryInsured(index, 'addressSameAsPatient')}
                        />
                        {!insurance.primaryInsured?.addressSameAsPatient && (
                          <>
                            <Input
                              name="streetAddress"
                              className="col-span-2"
                              label={iv.streetAddressLabel}
                              value={insurance.primaryInsured?.streetAddress}
                              onChange={onChangeInsurancePrimaryInsured(index, 'streetAddress')}
                            />
                            <Input
                              name="primaryInsuredAddress2"
                              className="col-span-2"
                              label="Address cont."
                              value={insurance.primaryInsured?.address2}
                              onChange={onChangeInsurancePrimaryInsured(index, 'address2')}
                            />
                            <Input
                              name="primaryInsuredCity"
                              label={iv.cityLabel}
                              value={insurance.primaryInsured?.city}
                              onChange={onChangeInsurancePrimaryInsured(index, 'city')}
                              className="col-span-2"
                            />
                            <Combobox
                              name="primaryInsuredState"
                              className="col-span-2"
                              label={
                                insurance.primaryInsured?.country === 'CAN' ? 'Province' : 'State'
                              }
                              value={insurance.primaryInsured?.state}
                              onChange={onChangeInsurancePrimaryInsured(index, 'state')}
                              options={
                                insurance.primaryInsured?.country === 'CAN'
                                  ? CANADIAN_PROVINCES
                                  : STATES
                              }
                              limit={1}
                              noDarkMode
                            />
                            <ZipCodeInput
                              name="primaryInsuredZip"
                              label={iv.zipCodeLabel}
                              className="col-span-2"
                              value={insurance.primaryInsured?.postalCode}
                              onChange={onChangeInsurancePrimaryInsured(index, 'postalCode')}
                              country={insurance.primaryInsured?.country || 'US'}
                            />
                            <Select
                              name="primaryInsuredCountry"
                              label={iv.country}
                              className="col-span-2"
                              value={insurance.primaryInsured?.country}
                              onChange={onChangeInsurancePrimaryInsured(index, 'country')}
                              options={COUNTRIES}
                              noDarkMode
                            />
                          </>
                        )}
                      </div>
                    </div>
                  )}
              </div>
              <div className="flex flex-col w-full gap-4 mt-10 sm:flex-row sm:h-64">
                <UploadInsuranceSide
                  side="front"
                  primaryColor={primaryColor}
                  language={language}
                  requestId={requestId}
                  validationCode={validationCode}
                  prMap={prMap || {}}
                  imagePreview={insurance.insuranceFrontPreview || null}
                  onChangeImagePreview={(url: string | null) =>
                    setInsurances(prev => {
                      const next = [...prev];
                      next[index] = { ...next[index], insuranceFrontPreview: url };

                      return next;
                    })
                  }
                  onUploadInsuranceCard={(url: string) =>
                    onUploadInsuranceCard('front', url, index)
                  }
                  onUploadStart={() => setUploadsInProgress(true)}
                  className="max-w-sm overflow-hidden shadow-lg border-gray-300 border rounded-md max-h-64"
                />
                <UploadInsuranceSide
                  side="back"
                  primaryColor={primaryColor}
                  language={language}
                  requestId={requestId}
                  validationCode={validationCode}
                  prMap={prMap || {}}
                  imagePreview={insurance.insuranceBackPreview || null}
                  onChangeImagePreview={(url: string | null) =>
                    setInsurances(prev => {
                      const next = [...prev];
                      next[index] = { ...next[index], insuranceBackPreview: url };

                      return next;
                    })
                  }
                  onUploadInsuranceCard={(url: string) => onUploadInsuranceCard('back', url, index)}
                  onUploadStart={() => setUploadsInProgress(true)}
                  className="max-w-sm overflow-hidden rounded shadow-lg max-h-64"
                />
              </div>
              {index !== insurances.length && <hr className="my-8" />}
            </div>
          ))}

          <div className="flex flex-col mt-16">
            <div className="mb-8 flex gap-4">
              {insurances.length < 3 && (
                <Button
                  icon={Icons.add}
                  text={v.addAnother}
                  onClick={addPolicy}
                  color={ButtonColors.plain}
                  disabled={isSubmitting || skipping}
                />
              )}
            </div>

            <div className="flex justify-between">
              <Button
                text={v.next}
                type="button"
                onClick={onSubmit}
                disabled={isSubmitting || skipping || uploadsInProgress}
                loading={isSubmitting || uploadsInProgress}
              />
              {uploadsInProgress && (
                <div className="ml-4 text-sm text-gray-600">
                  {language === 'es' ? 'Cargando imágenes...' : 'Uploading images...'}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StepInsurance;
